import { type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Endpoints, ScopedBoolean } from '@/api';
import { useClientToken, useCurrentSession, useStoreToken } from '@/services/auth';
import { useFetch } from '@/utils/fetch';

import { ArticleServiceType, getDisplayedStep } from './article';
import { ArticleWithRelations, RequestWithRelations } from './request';

export const getDisplayedSteps = (workflow: WorkflowPublic) =>
  workflow.nodes
    .map((node) => ('step' in node ? getDisplayedStep(node) : null))
    .filter((displayedStep) => !!displayedStep);

export const getServiceTypes = (workflow: WorkflowPublic): ArticleServiceType[] =>
  workflow.nodes
    .flatMap((node) =>
      'step' in node
        ? node.step === 'creation' &&
          'requireServiceChoice' in node.config &&
          !!node.config.requireServiceChoice
          ? node.config.requireServiceChoice.serviceOptions
          : node.step === 'service-choice'
            ? node.config.serviceOptions
            : node.step === 'analysis'
              ? node.config.serviceOptions
              : []
        : []
    )
    .filter((serviceTypeOrUndefined) => !!serviceTypeOrUndefined)
    .map((serviceType) => (serviceType === 'exchange-archival' ? 'exchange' : serviceType));

export const hasOnlinePaymentStep = (workflow: WorkflowPublic) =>
  workflow.nodes.some(
    (node) => 'step' in node && node.step === 'payment' && node.config.method === 'online'
  );

export const hasTransitStepsExceptClientPickup = (workflow: WorkflowPublic) =>
  workflow.nodes.some(
    (node) => 'step' in node && node.step === 'transit' && !node.config.clientPickup
  );

export const hasContactClientTask = (workflow: WorkflowPublic): boolean =>
  workflow.nodes.some(
    (node) =>
      'step' in node &&
      ((node.step === 'validation' && node.config.tasks?.contact_client?.enabled) ||
        (node.step === 'transit' && node.config.tasks?.contact_client?.enabled))
  );

export const hasStartRepairEnabled = (workflow: WorkflowPublic): boolean =>
  workflow.nodes.some(
    (node) => 'step' in node && node.step === 'repair' && node.config.startRepair?.enabled
  );

export const hasStartExchangeEnabled = (workflow: WorkflowPublic): boolean =>
  workflow.nodes.some(
    (node) => 'step' in node && node.step === 'exchange' && node.config.startExchange?.enabled
  );

export const hasTransitNotClientPickupStep = (workflow: WorkflowPublic): boolean =>
  workflow.nodes.some(
    (node) => 'step' in node && node.step === 'transit' && !node.config.clientPickup
  );

export const hasExchangeStep = (workflow: WorkflowPublic): boolean =>
  workflow.nodes.some((node) => 'step' in node && node.step === 'exchange');

export const getDefaultExchangeDuration = ({
  workflow,
  request,
}: {
  workflow: WorkflowPublic | undefined;
  request: RequestWithRelations | undefined;
}) => {
  const exchangeStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'exchange');
  const dueAtType = request?.dueAtType;

  return (
    exchangeStep?.config?.statusDueAt?.exchange.find(({ type }) => type === dueAtType)?.days ?? 0
  );
};

export const getAnalysisExchangeTypes = ({
  workflow,
  article,
}: {
  workflow: WorkflowPublic | undefined;
  article: ArticleWithRelations;
}) => {
  if (article.step?.step === 'analysis') {
    return article.step.config.exchangeTypes;
  }

  if (
    article.step?.step === 'processing' &&
    article.taskType === 'adjust_service_during_processing'
  ) {
    const analysisNode = workflow?.nodes.find((node) => 'step' in node && node.step === 'analysis');

    return analysisNode?.config.exchangeTypes;
  }

  return undefined;
};

export const getTraitsToActOnBehalfOfNonDigitalizedWorkshop = ({
  workflow,
}: {
  workflow: WorkflowPublic | undefined | null;
}) => {
  return workflow?.config.traitsToActOnBehalfOfNonDigitalizedWorkshop ?? ['store'];
};

export type WorkflowPublicWithRelations = Endpoints['GET /workflows/:id/public']['response'];
export type WorkflowPublic = Omit<WorkflowPublicWithRelations, 'conditionForms'>;
export type WorkflowListItem = Endpoints['GET /workflows']['response']['workflows'][number];

export type Workflow = Endpoints['GET /workflows/:id']['response'];

export const useWorkflows = (
  params?: Endpoints['GET /workflows']['query'],
  options: {
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /workflows']>();

  return useQuery({
    queryKey: ['workflows', params],
    queryFn: () => fetch('/workflows', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useWorkflow = (id?: string | null) => {
  const fetch = useFetch<Endpoints['GET /workflows/:id']>();

  return useQuery({
    queryKey: ['workflows', id],
    queryFn: () => fetch(`/workflows/${id}`),
    enabled: !!id,
  });
};

export const useWorkflowPublic = (id?: string | null) => {
  const fetch = useFetch<Endpoints['GET /workflows/:id/public']>();
  const { currentSession } = useCurrentSession();
  const storeToken = useStoreToken();
  const clientToken = useClientToken();

  return useQuery({
    queryKey: ['workflows', id, 'public'],
    queryFn: () => fetch(`/workflows/${id}/public`),
    enabled: !!id && (!!currentSession || !!storeToken || !!clientToken),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /workflows']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /workflows']['body']) =>
      fetch('/workflows', undefined, {
        method: 'POST',
        body,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};

export const useUpdateWorkflow = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /workflows/:id']>();

  return useMutation({
    mutationFn: ({
      workflowId,
      body,
    }: {
      workflowId: string;
      body: Endpoints['PATCH /workflows/:id']['body'];
    }) => fetch(`/workflows/${workflowId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};

export const useUpdateWorkflowConfig = (workflowId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /workflows/:id/config']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /workflows/:id/config']['body']) =>
      fetch(`/workflows/${workflowId}/config`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};

export const useUpdateWorkflowStep = (workflowId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /workflows/:id/steps/:stepId']>();

  return useMutation({
    mutationFn: ({
      stepId,
      ...body
    }: Endpoints['PATCH /workflows/:id/steps/:stepId']['body'] & { stepId: string }) =>
      fetch(`/workflows/${workflowId}/steps/${stepId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
};

/**
 * Converts a scoped boolean into a simple boolean by evaluating every matching
 * credentials scope and returning true if at least one of them is true (OR).
 * Scopes without a specific override (e.g. aftersales, organization, client,
 * stockLocation) use `default`.
 *
 * A stockLocation scope on the request's store (e.g. immediate exchange, where the
 * store's own stock is used) is ignored: the store override must apply.
 */
export const getScopedBoolean = ({
  scopedBoolean,
  credentialsScopes,
}: {
  scopedBoolean: ScopedBoolean | undefined;
  credentialsScopes: RequestWithRelations['scopes'] | undefined;
}) => {
  if (scopedBoolean === undefined) {
    return false;
  }

  if (typeof scopedBoolean === 'boolean') {
    return scopedBoolean;
  }

  if (!credentialsScopes?.length) {
    return scopedBoolean.default;
  }

  const storeEntityIds = new Set(
    credentialsScopes.filter(({ scope }) => scope === 'store').map(({ entityId }) => entityId)
  );

  const relevantScopes = credentialsScopes.filter(
    ({ scope, entityId }) => !(scope === 'stockLocation' && storeEntityIds.has(entityId))
  );

  const possibleValues = relevantScopes.map((scope) => {
    if (scope.scope === 'store') {
      return scope.external
        ? (scopedBoolean.externalStore ?? scopedBoolean.default)
        : (scopedBoolean.internalStore ?? scopedBoolean.default);
    }

    if (scope.scope === 'workshop') {
      return scope.external
        ? (scopedBoolean.externalWorkshop ?? scopedBoolean.default)
        : (scopedBoolean.internalWorkshop ?? scopedBoolean.default);
    }

    return scopedBoolean.default;
  });

  return possibleValues.some((value) => !!value);
};

const WORKFLOW_TYPES = [
  {
    id: 'client',
    label: msg({ id: 'workflow.type.client', message: 'Online client' }),
  },
  {
    id: 'client_via_store',
    label: msg({ id: 'workflow.type.client-via-store', message: 'In-store client' }),
  },
  {
    id: 'store',
    label: msg({ id: 'workflow.type.store', message: 'Store' }),
  },
] as const;

type WorkflowType = (typeof WORKFLOW_TYPES)[number]['id'];

export const getWorkflowTypeLabel = (workflowType: WorkflowType) => {
  return WORKFLOW_TYPES.find((type) => type.id === workflowType)!.label;
};

const STORE_TYPES = [
  {
    id: 'internal',
    label: msg({ id: 'workflow.store-type.internal', message: 'Internal' }),
  },
  {
    id: 'external',
    label: msg({ id: 'workflow.store-type.external', message: 'External' }),
  },
] as const;

type StoreType = (typeof STORE_TYPES)[number]['id'];

export const getWorkflowStoreTypeLabel = (storeType: StoreType) => {
  return STORE_TYPES.find((type) => type.id === storeType)!.label;
};

type WorkflowStep = Extract<Workflow['nodes'][number], { step: unknown }>['step'];

export const WORKFLOW_STEPS = {
  creation: {
    name: msg({ id: 'article.step.creation', message: 'Creation' }),
  },
  'service-choice': {
    name: msg({ id: 'article.step.service-choice', message: 'Diagnosis' }),
  },
  validation: {
    name: msg({ id: 'article.step.validation', message: 'Validation' }),
  },
  transit: {
    name: msg({ id: 'article.step.transit', message: 'Transit' }),
  },
  analysis: {
    name: msg({ id: 'article.step.analysis', message: 'Analysis' }),
  },
  payment: {
    name: msg({ id: 'article.step.payment', message: 'Payment' }),
  },
  refund: {
    name: msg({ id: 'article.step.refund', message: 'Refund' }),
  },
  repair: {
    name: msg({ id: 'article.step.repair', message: 'Repair' }),
  },
  exchange: {
    name: msg({ id: 'article.step.exchange', message: 'Exchange' }),
  },
  'quality-control': {
    name: msg({ id: 'article.step.quality-control', message: 'Quality control' }),
  },
  processing: {
    name: msg({ id: 'article.step.processing', message: 'Processing' }),
  },
  archival: {
    name: msg({ id: 'article.step.archival', message: 'Archival' }),
  },
} satisfies Record<WorkflowStep, { name: MessageDescriptor }>;

type WorkflowCondition = Extract<Workflow['nodes'][number], { condition: unknown }>['condition'];

export const WORKFLOW_CONDITIONS = {
  'delivery-choice': {
    label: msg({ id: 'workflow.condition.delivery-choice', message: 'Delivery choice' }),
    question: msg({
      id: 'workflow.condition.delivery-choice.question',
      message: 'Where should it be delivered to?',
    }),
  },
  'workshop-type': {
    label: msg({ id: 'workflow.condition.workshop-type', message: 'Workshop type' }),
    question: msg({
      id: 'workflow.condition.workshop-type.question',
      message: 'What type of workshop is assigned?',
    }),
  },
  'request-free': {
    label: msg({ id: 'workflow.condition.request-free', message: 'Request free' }),
    question: msg({
      id: 'workflow.condition.request-free.question',
      message: 'Is the request free?',
    }),
  },
  'article-location': {
    label: msg({ id: 'workflow.condition.article-location', message: 'Article location' }),
    question: msg({
      id: 'workflow.condition.article-location.question',
      message: 'Where is the article located?',
    }),
  },
  'service-type': {
    label: msg({ id: 'workflow.condition.service-type', message: 'Service type' }),
    question: msg({
      id: 'workflow.condition.service-type.question',
      message: 'What service type is needed?',
    }),
  },
  'article-at-assigned-workshop': {
    label: msg({
      id: 'workflow.condition.article-at-assigned-workshop',
      message: 'Article at assigned workshop',
    }),
    question: msg({
      id: 'workflow.condition.article-at-assigned-workshop.question',
      message: 'Is the article at the assigned workshop?',
    }),
  },
  'workshop-non-digitalized': {
    label: msg({
      id: 'workflow.condition.workshop-non-digitalized',
      message: 'Workshop non digitalized',
    }),
    question: msg({
      id: 'workflow.condition.workshop-non-digitalized.question',
      message: 'Is the workshop non-digitalized?',
    }),
  },
  'all-actions-have-workshop-price': {
    label: msg({
      id: 'workflow.condition.all-actions-have-workshop-price',
      message: 'All actions have workshop price',
    }),
    question: msg({
      id: 'workflow.condition.all-actions-have-workshop-price.question',
      message: 'Do all actions have a workshop price?',
    }),
  },
  'all-actions-have-organization-price': {
    label: msg({
      id: 'workflow.condition.all-actions-have-organization-price',
      message: 'All actions have organization price',
    }),
    question: msg({
      id: 'workflow.condition.all-actions-have-organization-price.question',
      message: 'Do all actions have an organization price?',
    }),
  },
  'all-actions-have-workshop-and-organization-price': {
    label: msg({
      id: 'workflow.condition.all-actions-have-workshop-and-organization-price',
      message: 'All actions have workshop and organization price',
    }),
    question: msg({
      id: 'workflow.condition.all-actions-have-workshop-and-organization-price.question',
      message: 'Do all actions have a workshop and organization price?',
    }),
  },
  'payment-price-types-are-still-unpaid': {
    label: msg({
      id: 'workflow.condition.payment-price-types-are-still-unpaid',
      message: 'Payment price types are still unpaid',
    }),
    question: msg({
      id: 'workflow.condition.payment-price-types-are-still-unpaid.question',
      message: 'Are the payment price types still unpaid?',
    }),
  },
  'refund-or-payment': {
    label: msg({
      id: 'workflow.condition.refund-or-payment',
      message: 'Refund, payment or none',
    }),
    question: msg({
      id: 'workflow.condition.refund-or-payment.question',
      message: 'Is a refund, payment, or none needed?',
    }),
  },
  'request-is-fully-paid': {
    label: msg({
      id: 'workflow.condition.request-is-fully-paid',
      message: 'Request is fully paid',
    }),
    question: msg({
      id: 'workflow.condition.request-is-fully-paid.question',
      message: 'Is the request fully paid?',
    }),
  },
  'is-immediate-exchange': {
    label: msg({
      id: 'workflow.condition.is-immediate-exchange',
      message: 'Immediate exchange',
    }),
    question: msg({
      id: 'workflow.condition.is-immediate-exchange.question',
      message: 'Is it an immediate exchange?',
    }),
  },
} satisfies Record<WorkflowCondition, { label: MessageDescriptor; question: MessageDescriptor }>;

export const WORKFLOW_STATUS_DUE_AT_TYPES = [
  {
    id: 'serviceChoice',
    label: msg({
      id: 'workflow.status-due-at.type.service-choice',
      message: 'Service choice',
    }),
  },
  {
    id: 'dispatch',
    label: msg({
      id: 'workflow.status-due-at.type.dispatch',
      message: 'Dispatch',
    }),
  },
  {
    id: 'validation',
    label: msg({
      id: 'workflow.status-due-at.type.validation',
      message: 'Validation',
    }),
  },
  {
    id: 'preparation',
    label: msg({
      id: 'workflow.status-due-at.type.preparation',
      message: 'Preparation',
    }),
  },
  {
    id: 'transit',
    label: msg({
      id: 'workflow.status-due-at.type.transit',
      message: 'Transit',
    }),
  },
  {
    id: 'verification',
    label: msg({
      id: 'workflow.status-due-at.type.verification',
      message: 'Verification',
    }),
  },
  {
    id: 'analysis',
    label: msg({
      id: 'workflow.status-due-at.type.analysis',
      message: 'Analysis',
    }),
  },
  {
    id: 'payment',
    label: msg({
      id: 'workflow.status-due-at.type.payment',
      message: 'Payment',
    }),
  },
  {
    id: 'startRepair',
    label: msg({
      id: 'workflow.status-due-at.type.start-repair',
      message: 'Start repair',
    }),
  },
  {
    id: 'repair',
    label: msg({
      id: 'workflow.status-due-at.type.repair',
      message: 'Repair',
    }),
  },
  {
    id: 'startExchange',
    label: msg({
      id: 'workflow.status-due-at.type.start-exchange',
      message: 'Start exchange',
    }),
  },
  {
    id: 'exchange',
    label: msg({
      id: 'workflow.status-due-at.type.exchange',
      message: 'Exchange',
    }),
  },
  {
    id: 'qualityControl',
    label: msg({
      id: 'workflow.status-due-at.type.quality-control',
      message: 'Quality control',
    }),
  },
  {
    id: 'processing',
    label: msg({
      id: 'workflow.status-due-at.type.processing',
      message: 'Processing',
    }),
  },
  {
    id: 'serviceAdjustment',
    label: msg({
      id: 'workflow.status-due-at.type.service-adjustment',
      message: 'Service adjustment',
    }),
  },
];
